import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { GleapService } from '@core/services/gleap.service';
import {
  IARA_LAUNCHER_DRAG_THRESHOLD_PX,
  IARA_LAUNCHER_LAYOUT,
  IARA_LAUNCHER_POSITION_LABEL,
  IARA_LAUNCHER_POSITIONS,
  IARA_LAUNCHER_TEACH_MS,
} from '../../constants/iara-launcher-positions';
import type { IaraLauncherPosition } from '../../models/iara-launcher-position.model';
import { IaraPanelStateService } from '../../services/iara-panel-state.service';
import {
  cycleIaraLauncherPosition,
  nearestIaraLauncherPosition,
  stepIaraLauncherPosition,
} from '../../utils/iara-launcher-position';
import { PalantirIaraGlyphComponent, PalantirIconComponent } from '@flux/design-system';

/**
 * Gatilho flutuante da Iara: uma pill rotulada que abre a conversa em um clique.
 *
 * O rótulo é visível em repouso, e não só no hover, porque ele É a affordance: um
 * círculo com glifo obriga o usuário a descobrir o que faz. A identidade vem do
 * gradiente de marca (`--palantir-gradient-iara-orb`) e do glifo, não de uma
 * família tipográfica nova — o tom do produto é "profissional, denso, funcional".
 *
 * Decisões que valem registrar:
 *
 * - **A pill abre a Iara direto.** O rótulo promete Iara; abrir um menu de escolha
 *   ali seria promessa quebrada.
 * - **Suporte e ocultar são satélites** revelados no hover ou no foco, e ficam FORA
 *   do DOM até então — melhor que invisíveis-mas-focáveis, que confunde o teclado.
 * - **Nada aqui é modal.** É revelação progressiva, não um disclosure com foco
 *   preso: sem Escape, sem clique-fora, sem roving tabindex. Sair do widget (com o
 *   ponteiro ou tabulando) já recolhe.
 * - **Os satélites vêm DEPOIS da pill no DOM**, e o eixo é invertido no CSS. Assim
 *   a ordem de tabulação segue a visual de baixo para cima e tabular a partir da
 *   pill alcança os satélites, em vez de exigir Shift+Tab.
 * - **`role="group"`, não `role="menu"`.** `menu`/`menuitem` exige navegação por
 *   setas; são botões comuns, cada um um tab stop.
 * - **Ocultar dura até recarregar.** Não persiste: hoje não existe tela para
 *   trazer o widget de volta, e um estado permanente sem desfazer seria armadilha.
 * - **Este componente NÃO mexe na visibilidade do launcher do Gleap.** Quem o
 *   esconde é o boot da aplicação (`main.ts`), de uma vez: o gatilho não é montado
 *   em toda rota, e esconder ao montar/devolver ao destruir fazia o botão do Gleap
 *   voltar a aparecer. Daqui só sai `startConversation()`, sob demanda.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PalantirIaraGlyphComponent, PalantirIconComponent],
  selector: 'app-iara-floating-launcher',
  standalone: true,
  templateUrl: './iara-floating-launcher.component.html',
})
export class IaraFloatingLauncherComponent {
  /** Painel da Iara aberto: a pill passa a fechá-lo. */
  public readonly iaraOpen = input(false);

  public readonly askIara = output();

  private readonly gleap = inject(GleapService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly panel = inject(IaraPanelStateService);

  private readonly expanded = signal(false);
  private readonly dismissed = signal(false);
  private readonly moving = signal(false);
  private readonly announcement = signal('');

  /**
   * A pill já se apresentou. Começa `false` para o rótulo aparecer no carregamento e
   * recolher em seguida: o usuário descobre o que o botão faz sem que ele fique
   * ocupando espaço para sempre.
   */
  private readonly taught = signal(false);

  /** Estado do gesto. Campo simples, não signal: nada no template depende dele. */
  private drag: { pointerId: number; startPosition: IaraLauncherPosition; startY: number } | null =
    null;

  /**
   * O `click` dispara DEPOIS do `pointerup`, então um arraste bem-sucedido sempre gera um
   * clique no fim. Sem suprimi-lo, o ciclo do clique rodava em cima do encaixe já
   * comitado e o botão parava uma posição adiante da soltada.
   *
   * Só é ligado no caminho em que o clique é GARANTIDO (`pointerup` depois de arrastar).
   * Em cancelamento não: `pointercancel` e Escape não produzem clique, então a flag
   * ficaria pendurada e engoliria o próximo clique legítimo — inclusive um Enter no
   * teclado, que dispara `click` sem `pointerdown` nenhum. Zerada também a cada
   * `pointerdown`, como rede de segurança.
   */
  private suppressClick = false;

  /** Satélites (suporte, mover, ocultar) entram no DOM só com hover ou foco no widget. */
  protected readonly showSatellites = this.expanded.asReadonly();
  protected readonly isDismissed = this.dismissed.asReadonly();
  protected readonly isMoving = this.moving.asReadonly();
  protected readonly moveAnnouncement = this.announcement.asReadonly();

  protected readonly positions = IARA_LAUNCHER_POSITIONS;

  /** Posição a renderizar: a do arraste em curso, se houver. */
  protected readonly position = this.panel.effectivePosition;

  protected readonly layout = computed(() => IARA_LAUNCHER_LAYOUT[this.position()]);

  /**
   * String única, não array: `[class]` com array trata cada item como UM nome de
   * classe, então um item com espaço (`'bottom-20 right-4'`) é descartado inteiro.
   */
  protected readonly launcherClasses = computed(
    () => `${this.layout().launcher} ${this.layout().stack}`,
  );

  protected readonly positionDescription = computed(
    () => `Posição atual: ${IARA_LAUNCHER_POSITION_LABEL[this.position()]}.`,
  );

  protected readonly label = computed(() =>
    this.iaraOpen() ? 'Fechar a conversa' : 'Pergunte à Iara',
  );

  /**
   * Rótulo aberto durante a apresentação e sempre que o widget está sob hover ou foco —
   * depois de recolhido, é o hover que volta a dizer o que o botão faz.
   */
  protected readonly labelVisible = computed(() => !this.taught() || this.expanded());

  /**
   * Recolhido, a largura fica `px-3 + glifo 20 + px-3` = 44px, igual à altura (`h-11`):
   * um círculo exato. Anima `max-width` e `margin` em vez de `width`, porque a largura de
   * um flex container é dirigida pelo conteúdo e não transiciona.
   */
  protected readonly labelClasses = computed(() =>
    this.labelVisible() ? 'ml-2 max-w-[10rem] opacity-100' : 'ml-0 max-w-0 opacity-0',
  );

  public constructor() {
    // Recolhe depois da apresentação. `setTimeout` e não animação de CSS porque o estado
    // precisa ser observável (o hover reabre o rótulo, e o teste verifica os dois lados).
    const teachTimer = globalThis.setTimeout(() => this.taught.set(true), IARA_LAUNCHER_TEACH_MS);

    // Rede de segurança: se o componente morrer no meio de um arraste, o estado do
    // gesto não pode sobreviver. Mesma disciplina do `TrackDragDirective`.
    inject(DestroyRef).onDestroy(() => {
      globalThis.clearTimeout(teachTimer);
      this.drag = null;
      this.suppressClick = false;
      this.moving.set(false);
    });
  }

  protected layoutFor(position: IaraLauncherPosition): string {
    return IARA_LAUNCHER_LAYOUT[position].launcher;
  }

  protected onExpand(): void {
    this.expanded.set(true);
  }

  /**
   * O ponteiro sair não recolhe nada se o foco ficou dentro: senão o satélite
   * focado sai do DOM e a posição do teclado se perde por um movimento de mouse.
   */
  protected onCollapse(): void {
    // Durante o arraste a pill PULA entre encaixes e sai de baixo do cursor, o que
    // dispara `mouseleave`. Recolher aqui destruiria a alça no meio do gesto e mataria
    // a captura de ponteiro.
    if (this.moving() || this.containsFocus()) return;

    this.expanded.set(false);
  }

  /**
   * Só recolhe quando o foco sai do widget de fato. Sem esta checagem, tabular da
   * pill para um satélite recolheria o conjunto no meio do caminho.
   */
  protected onFocusOut(event: FocusEvent): void {
    if (this.moving()) return;

    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) return;

    this.expanded.set(false);
  }

  protected onAskIara(): void {
    this.askIara.emit();
  }

  protected onTalkToSupport(): void {
    this.expanded.set(false);
    this.gleap.startConversation();
  }

  /**
   * Clique na alça cicla para o encaixe seguinte. Um botão cujo clique não faz nada é
   * armadilha, e ciclar deixa o modelo "existem três posições" descobrível sem arrastar.
   *
   * Suprimido quando o gesto virou arraste — senão o `click` que segue o `pointerup`
   * moveria uma segunda vez.
   */
  protected onMoveClick(): void {
    // Consome a supressão do arraste que acabou de terminar. Não dá para usar o
    // `moving()` aqui: ele já foi desligado no `pointerup`, que vem antes do clique.
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }

    this.commit(cycleIaraLauncherPosition(this.panel.position()));
  }

  /** Setas dão um passo na ordem visual, sem dar a volta. */
  protected onMoveKey(event: KeyboardEvent): void {
    // Com um arraste em curso, teclado e ponteiro comitariam de bases diferentes — a seta
    // parte da posição PERSISTIDA, o arraste da pré-visualização — e o `pointerup`
    // comitaria de novo por cima. Um gesto por vez.
    if (this.drag) return;

    const direction = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : null;
    if (direction === null) return;

    // Sem isto a página rola junto com o gesto.
    event.preventDefault();

    this.commit(stepIaraLauncherPosition(this.panel.position(), direction));
  }

  protected onMovePointerDown(event: PointerEvent): void {
    // Gesto novo começa sem supressão herdada do anterior.
    this.suppressClick = false;

    this.drag = {
      pointerId: event.pointerId,
      startPosition: this.panel.position(),
      startY: event.clientY,
    };

    // Opcional no jsdom, que não implementa captura de ponteiro.
    (event.target as Element | null)?.setPointerCapture?.(event.pointerId);
  }

  protected onMovePointerMove(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    // Abaixo do limiar o gesto ainda é um clique; virar arraste aqui faria um clique
    // trêmulo encaixar no mesmo lugar e parecer que o botão não fez nada.
    if (Math.abs(event.clientY - this.drag.startY) < IARA_LAUNCHER_DRAG_THRESHOLD_PX) return;

    this.moving.set(true);
    this.panel.previewMove(nearestIaraLauncherPosition(event.clientY, globalThis.innerHeight));
  }

  protected onMovePointerUp(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;

    const moved = this.moving();
    const target = this.position();
    this.drag = null;

    // Abaixo do limiar o gesto foi um clique: deixa o `click` ciclar normalmente.
    if (!moved) return;

    this.commit(target);
    this.moving.set(false);
    this.suppressClick = true;
  }

  /** `pointercancel`, perda de captura e Escape voltam ao ponto de partida. */
  protected onMoveCancel(): void {
    if (!this.drag) return;

    const wasMoving = this.moving();
    this.drag = null;
    this.moving.set(false);
    this.panel.previewMove(null);

    if (!wasMoving) return;

    // Sem suprimir clique aqui: cancelamento não gera `click`, então a flag ficaria
    // pendurada e mataria o próximo clique de verdade.
    this.announcement.set('Movimentação cancelada.');
  }

  /**
   * Sai da frente agora e volta no próximo carregamento.
   *
   * NÃO devolve o launcher do Gleap: ele fica escondido desde o boot, e trazê-lo de
   * volta aqui era justamente o que fazia o botão do suporte reaparecer. Enquanto
   * isto está oculto não há caminho até o suporte pela tela — consequência aceita
   * de o launcher do Gleap nunca aparecer sozinho.
   */
  protected onDismiss(): void {
    this.dismissed.set(true);
  }

  private commit(position: IaraLauncherPosition): void {
    this.panel.moveTo(position);
    this.announcement.set(`Botão da Iara movido para o ${IARA_LAUNCHER_POSITION_LABEL[position]}.`);
  }

  private containsFocus(): boolean {
    const element = this.host.nativeElement;

    return document.activeElement !== null && element.contains(document.activeElement);
  }
}
